import { useState } from 'react';
import CampoArea from '@/components/base/CampoArea';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import { TIPOS_PERGUNTA, TIPOS_COM_OPCOES, TIPO_LABEL } from '../constants';
import type { Pergunta } from '../types';

interface CartaoPerguntaProps {
  pergunta: Pergunta;
  indice: number;
  condicionaveis: Pergunta[];
  selecionado: boolean;
  podeSubir: boolean;
  podeDescer: boolean;
  erroEnunciado?: string;
  erroOpcoes?: string;
  aoSelecionar: () => void;
  aoMudar: (patch: Partial<Pergunta>) => void;
  aoSubir: () => void;
  aoDescer: () => void;
  aoRemover: () => void;
  aoDuplicar: () => void;
}

function rotuloCondicaoValor(pergunta: Pergunta, valor: string | null): string {
  if (valor === 'true') return 'Sim';
  if (valor === 'false') return 'Não';
  return valor ?? '';
}

export default function CartaoPergunta({
  pergunta: p,
  indice,
  condicionaveis,
  selecionado,
  podeSubir,
  podeDescer,
  erroEnunciado,
  erroOpcoes,
  aoSelecionar,
  aoMudar,
  aoSubir,
  aoDescer,
  aoRemover,
  aoDuplicar,
}: CartaoPerguntaProps) {
  const [mostrarMais, setMostrarMais] = useState(false);
  const temOpcoes = TIPOS_COM_OPCOES.includes(p.tipo);
  const ehLista = p.tipo === 'lista';
  const condicao = condicionaveis.find((c) => c.chave === p.condicaoChave);

  const opcoesCondicaoValor: { valor: string; rotulo: string }[] = condicao
    ? condicao.tipo === 'sim_nao'
      ? [
          { valor: 'true', rotulo: 'Sim' },
          { valor: 'false', rotulo: 'Não' },
        ]
      : condicao.opcoes.map((o) => ({ valor: o, rotulo: o }))
    : [];

  function mudarTipo(tipo: Pergunta['tipo']) {
    const patch: Partial<Pergunta> = { tipo };
    if (!TIPOS_COM_OPCOES.includes(tipo)) {
      patch.opcoes = [];
      patch.condicaoChave = null;
      patch.condicaoValor = null;
    }
    aoMudar(patch);
  }

  function aoEscolherCondicao(chave: string) {
    if (!chave) {
      aoMudar({ condicaoChave: null, condicaoValor: null });
      return;
    }
    aoMudar({ condicaoChave: chave, condicaoValor: null });
  }

  return (
    <div
      className={`rounded-lg border bg-white transition-colors ${
        selecionado ? 'border-primary-300' : 'border-secondary-200'
      }`}
    >
      {/* Linha recolhida */}
      <button
        type="button"
        onClick={aoSelecionar}
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-secondary-400">
          <i className="ri-drag-move-2-line text-lg" />
        </span>
        <span className="w-6 shrink-0 text-sm font-medium text-secondary-500">{indice + 1}</span>
        <span
          className={`min-w-0 flex-1 truncate text-[15px] ${
            p.enunciado ? 'text-foreground-900' : 'text-secondary-400'
          }`}
        >
          {p.enunciado || 'Pergunta sem título'}
        </span>
        <span className="hidden shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700 sm:inline-block">
          {TIPO_LABEL[p.tipo]}
        </span>
        {p.obrigatoria && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-danger-500"
            title="Obrigatória"
            aria-label="Obrigatória"
          />
        )}
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center text-secondary-500 transition-transform ${
            selecionado ? 'rotate-180' : ''
          }`}
        >
          <i className="ri-arrow-down-s-line text-lg" />
        </span>
      </button>

      {/* Corpo expandido */}
      {selecionado && (
        <div className="space-y-5 border-t border-secondary-100 px-4 py-4">
          <CampoArea
            id={`pergunta-${indice}-enunciado`}
            name="enunciado"
            label="Pergunta"
            value={p.enunciado}
            onChange={(v) => aoMudar({ enunciado: v })}
            required
            error={erroEnunciado || null}
            placeholder="Você possui alguma restrição alimentar?"
          />

          {/* Tipo + Obrigatória na mesma linha */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <label
                htmlFor={`pergunta-${indice}-tipo`}
                className="block text-sm font-medium text-foreground-900"
              >
                Tipo de resposta
              </label>
              <select
                id={`pergunta-${indice}-tipo`}
                value={p.tipo}
                onChange={(e) => mudarTipo(e.target.value as Pergunta['tipo'])}
                className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none focus:border-primary-500"
              >
                {TIPOS_PERGUNTA.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground-900">Obrigatória</span>
              <button
                type="button"
                onClick={() => aoMudar({ obrigatoria: !p.obrigatoria })}
                aria-label="Obrigatória"
                className={`relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
                  p.obrigatoria ? 'bg-primary-500' : 'bg-secondary-300'
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                    p.obrigatoria ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          </div>

          {p.tipo === 'foto' && (
            <p className="text-sm text-secondary-500">
              A pessoa tira ou escolhe uma foto. Serve para identificação.
            </p>
          )}

          {/* Editor de opções */}
          {temOpcoes && (
            <div>
              <span className="block text-sm font-medium text-foreground-900">
                {ehLista ? 'Colunas da lista' : 'Opções de resposta'}
              </span>
              {ehLista && (
                <p className="mt-0.5 text-xs text-secondary-500">
                  Cada coluna vira uma caixinha por linha. Ex.: Nome, Idade.
                </p>
              )}
              <div className="mt-2 space-y-2">
                {p.opcoes.map((opcao, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opcao}
                      onChange={(e) => {
                        const opcoes = p.opcoes.map((o, j) => (j === oi ? e.target.value : o));
                        aoMudar({ opcoes });
                      }}
                      placeholder={ehLista ? `Coluna ${oi + 1}` : `Opção ${oi + 1}`}
                      className="w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => aoMudar({ opcoes: p.opcoes.filter((_, j) => j !== oi) })}
                      aria-label={ehLista ? 'Remover coluna' : 'Remover opção'}
                      className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-danger-600"
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => aoMudar({ opcoes: [...p.opcoes, ''] })}
                className="mt-2 flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                {ehLista ? 'Adicionar coluna' : 'Adicionar opção'}
              </button>
              {erroOpcoes && (
                <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                  {erroOpcoes}
                </p>
              )}
            </div>
          )}

          {/* Mais opções */}
          <div>
            <button
              type="button"
              onClick={() => setMostrarMais((v) => !v)}
              className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-800"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i
                  className={`ri-add-line transition-transform ${mostrarMais ? 'rotate-45' : ''}`}
                />
              </span>
              Mais opções
            </button>

            {mostrarMais && (
              <div className="mt-4 space-y-5 rounded-md border border-secondary-200 bg-background-50 p-4">
                <CampoArea
                  id={`pergunta-${indice}-desc`}
                  name="descricao"
                  label="Texto de ajuda (opcional)"
                  help="Aparece em cinza abaixo da pergunta, para orientar quem responde."
                  value={p.descricao}
                  onChange={(v) => aoMudar({ descricao: v })}
                  placeholder="Marque todas que se aplicam."
                />

                <div>
                  <span className="block text-sm font-medium text-foreground-900">
                    Quando esta pergunta deve aparecer?
                  </span>
                  <div className="mt-1">
                    <OpcaoSelecao
                      tipo="radio"
                      checked={!p.condicaoChave}
                      onChange={() => aoMudar({ condicaoChave: null, condicaoValor: null })}
                    >
                      Sempre
                    </OpcaoSelecao>
                    <OpcaoSelecao
                      tipo="radio"
                      checked={!!p.condicaoChave}
                      onChange={() => {
                        if (condicionaveis.length > 0) {
                          aoEscolherCondicao(condicionaveis[0].chave);
                        }
                      }}
                    >
                      Só em certos casos
                    </OpcaoSelecao>
                  </div>

                  {p.condicaoChave && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-foreground-900">
                          Depende da pergunta:
                        </label>
                        <select
                          value={p.condicaoChave}
                          onChange={(e) => aoEscolherCondicao(e.target.value)}
                          className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                        >
                          {condicionaveis.map((c) => (
                            <option key={c.chave} value={c.chave}>
                              {c.enunciado || '(pergunta sem título)'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-900">
                          Quando a resposta for:
                        </label>
                        <select
                          value={p.condicaoValor ?? ''}
                          onChange={(e) => aoMudar({ condicaoValor: e.target.value || null })}
                          className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
                        >
                          {opcoesCondicaoValor.map((o) => (
                            <option key={o.valor} value={o.valor}>
                              {o.rotulo}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {condicao && p.condicaoValor && (
                    <p className="mt-2 text-sm text-foreground-700">
                      Esta pergunta só aparece quando{' '}
                      <strong>&quot;{condicao.enunciado}&quot;</strong> for{' '}
                      <strong>&quot;{rotuloCondicaoValor(condicao, p.condicaoValor)}&quot;</strong>.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Rodapé: reordenar + duplicar + remover */}
          <div className="flex items-center justify-between border-t border-secondary-100 pt-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={aoSubir}
                disabled={!podeSubir}
                aria-label="Mover para cima"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <i className="ri-arrow-up-s-line text-lg" />
              </button>
              <button
                type="button"
                onClick={aoDescer}
                disabled={!podeDescer}
                aria-label="Mover para baixo"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <i className="ri-arrow-down-s-line text-lg" />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={aoDuplicar}
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-800"
              >
                Duplicar
              </button>
              <button
                type="button"
                onClick={aoRemover}
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-danger-600 transition-colors hover:text-danger-700"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}